import React from 'react';

export interface Idea {
  section: string;
  ideaNumber: number;
  ideaName: string;
  description: string;
  implementation?: string;
  benefits?: string;
  principles?: string;
  combination?: string;
  synergy?: string;
}

export interface Top10Idea {
  rank: number;
  name: string;
  description: string;
  original_source: string;
  overall_score: number;
  key_strengths: string[];
  areas_for_improvement: string[];
  implementation_complexity: string;
  innovation_level: string;
  risk_assessment: string;
  quality_threshold_check: string;
  individual_scores: {
    criterion: string;
    score: number;
    justification: string;
  }[];
  financial_analysis?: {
    revenue_potential_year_1: string;
    revenue_potential_year_3: string;
    revenue_potential_year_5: string;
    break_even_timeline: string;
    roi_potential: string;
    payback_period: string;
    net_present_value: string;
    investment_required: string;
    cost_structure: string;
    pricing_strategy: string;
    revenue_model: string;
    financial_risk_factors: string[];
    sensitivity_analysis: string;
  };
  market_analysis?: {
    target_market_size: string;
    market_growth_rate: string;
    market_penetration_potential: string;
    competitive_landscape: string;
    customer_acquisition_cost: string;
    customer_lifetime_value: string;
    go_to_market_strategy: string;
  };
}

export interface MarketIntelligence {
  industry_overview?: string;
  market_size_and_growth?: string;
  key_trends?: string[];
  market_maturity?: string;
  regulatory_environment?: string;
}

export interface CompetitiveLandscape {
  major_players?: any[];
  competitive_gaps?: string[];
  barriers_to_entry?: string[];
  competitive_intensity?: string;
}

export interface ParsedData {
  sections: { [key: string]: Idea[] };
  totalIdeas: number;
  top10Ideas?: Top10Idea[];
  principles?: any[];
  function_based_ideas?: any[];
  system_operator_ideas?: any[];
  bottleneck_solution_ideas?: any[];
  benchmark_based_ideas?: any[];
  framework_based_ideas?: any[];
  custom_combination_ideas?: any[];
  combination_ideas?: any[];
  triz_blending_ideas?: any[];
  trisociation_ideas?: any[];
  enhanced_idea?: any[];
  improved_ideas?: any[];
  market_intelligence_summary?: MarketIntelligence;
  competitive_landscape?: CompetitiveLandscape;
  customer_intelligence?: any;
  technology_landscape?: any;
  market_opportunities?: any;
  strategic_recommendations?: any;
  [key: string]: any;
}

export class TrizIdeaParser {
  /**
   * Main parser method that extracts all data including top 10 ideas
   */
  public parse(text: string): ParsedData {
    console.log('TrizIdeaParser - Starting parse with text length:', text.length);
    const jsonObjects = this.extractJsonObjects(text);
    console.log('TrizIdeaParser - Extracted JSON objects:', {
      count: jsonObjects.length,
      objectTypes: jsonObjects.map(obj => typeof obj),
      firstObjectKeys: jsonObjects[0] ? Object.keys(jsonObjects[0]).slice(0, 10) : []
    });
    
    if (jsonObjects.length === 0) {
      throw new Error("Could not find any JSON objects in the provided text.");
    }

    // Track top_selected_ideas merging
    let totalTop10IdeasBeforeMerge = 0;
    let top10IdeasByAgent: any[] = [];
    
    // Pre-scan for top_selected_ideas
    jsonObjects.forEach((obj, index) => {
      if (obj && obj.top_selected_ideas && Array.isArray(obj.top_selected_ideas)) {
        totalTop10IdeasBeforeMerge += obj.top_selected_ideas.length;
        top10IdeasByAgent.push({
          index,
          agentName: obj.agent_name || `Object ${index + 1}`,
          selectionRange: obj.selection_range || 'Unknown',
          count: obj.top_selected_ideas.length,
          ranks: obj.top_selected_ideas.map((idea: any) => idea.rank)
        });
      }
    });
    
    console.log('TrizIdeaParser - Pre-merge top_selected_ideas scan:', {
      totalIdeasBeforeMerge: totalTop10IdeasBeforeMerge,
      agentBlocks: top10IdeasByAgent,
      expectedTotal: 10
    });

    const mergedData = jsonObjects.reduce((acc, obj, index) => {
      if (obj && typeof obj === 'object') {
        const currentObjectInfo = {
          objectKeys: Object.keys(obj),
          hasTopSelectedIdeas: !!obj.top_selected_ideas,
          topSelectedIdeasLength: obj.top_selected_ideas?.length,
          hasPrinciples: !!obj.principles,
          principlesLength: obj.principles?.length,
          agentName: obj.agent_name,
          selectionRange: obj.selection_range
        };
        
        console.log(`TrizIdeaParser - Processing JSON object ${index + 1}/${jsonObjects.length}:`, currentObjectInfo);
        
        // Handle principles
        if (obj.principles) {
          if (!acc.principles) acc.principles = [];
          acc.principles.push(...obj.principles);
        }
        
        // Handle top_selected_ideas specially to avoid overwriting
        if (obj.top_selected_ideas && Array.isArray(obj.top_selected_ideas)) {
          if (!acc.top_selected_ideas) acc.top_selected_ideas = [];
          
          const beforeCount = acc.top_selected_ideas.length;
          const newIdeas = obj.top_selected_ideas;
          const newIdeasInfo = newIdeas.map((idea: any) => ({
            rank: idea.rank,
            name: idea.idea_name || idea.name || 'Unnamed',
            hasScore: !!idea.overall_score,
            source: obj.agent_name || `Object ${index + 1}`
          }));
          
          console.log(`TrizIdeaParser - MERGING top_selected_ideas from object ${index + 1}:`, {
            agentName: obj.agent_name,
            selectionRange: obj.selection_range,
            beforeCount,
            newIdeasCount: newIdeas.length,
            newIdeasInfo,
            newIdeasRanks: newIdeas.map((idea: any) => idea.rank).sort((a: number, b: number) => a - b)
          });
          
          // Actually merge the ideas
          acc.top_selected_ideas.push(...newIdeas);
          
          const afterCount = acc.top_selected_ideas.length;
          console.log(`TrizIdeaParser - AFTER merging:`, {
            afterCount,
            totalIdeasNow: afterCount,
            allRanksNow: acc.top_selected_ideas.map((idea: any) => idea.rank).sort((a: number, b: number) => a - b),
            expectedTotal: 10,
            mergeSuccessful: afterCount === beforeCount + newIdeas.length
          });
        }
        
        // Merge the rest of the properties (except those we're handling specially)
        Object.keys(obj).forEach(key => {
          if (key !== 'top_selected_ideas' && key !== 'principles') {
            // For agent-specific fields, store them with agent prefix if multiple agents
            if (jsonObjects.length > 1 && (key === 'agent_name' || key === 'selection_range' || key === 'evaluation_criteria')) {
              const agentPrefix = obj.agent_name ? obj.agent_name.replace(/\s+/g, '_').toLowerCase() : `agent_${index + 1}`;
              acc[`${agentPrefix}_${key}`] = obj[key];
            } else {
              acc[key] = obj[key];
            }
          }
        });
      }
      return acc;
    }, {} as any);
    
    // Final validation of merged top_selected_ideas
    if (mergedData.top_selected_ideas) {
      const finalRanks = mergedData.top_selected_ideas.map((idea: any) => idea.rank).sort((a: number, b: number) => a - b);
      const uniqueRanks = [...new Set(finalRanks)];
      const missingRanks = [];
      for (let i = 1; i <= 10; i++) {
        if (!finalRanks.includes(i)) {
          missingRanks.push(i);
        }
      }
      
      console.log('TrizIdeaParser - FINAL top_selected_ideas validation:', {
        totalIdeas: mergedData.top_selected_ideas.length,
        ranks: finalRanks,
        uniqueRanks,
        hasDuplicateRanks: uniqueRanks.length !== finalRanks.length,
        missingRanks,
        hasAllTop10: missingRanks.length === 0 && mergedData.top_selected_ideas.length === 10
      });
      
      if (missingRanks.length > 0) {
        console.warn('TrizIdeaParser - WARNING: Missing ranks in top_selected_ideas:', missingRanks);
      }
    }
    
    // Alternative approach: If the standard merge didn't get all top 10 ideas, try direct merge
    if (!mergedData.top_selected_ideas || mergedData.top_selected_ideas.length < 10) {
      console.log('TrizIdeaParser - Standard merge incomplete, trying direct merge of top_selected_ideas');
      const directlyMergedTop10 = this.mergeTopSelectedIdeas(jsonObjects);
      if (directlyMergedTop10.length > (mergedData.top_selected_ideas?.length || 0)) {
        console.log('TrizIdeaParser - Direct merge found more ideas, using direct merge result');
        mergedData.top_selected_ideas = directlyMergedTop10;
      }
    }
    
    // Final fallback: Use dedicated top 10 parser if still incomplete
    if (!mergedData.top_selected_ideas || mergedData.top_selected_ideas.length < 10) {
      console.log('TrizIdeaParser - Still incomplete, using dedicated parseTop10Ideas method');
      const dedicatedTop10Result = this.parseTop10Ideas(text);
      if (dedicatedTop10Result.length > (mergedData.top_selected_ideas?.length || 0)) {
        console.log('TrizIdeaParser - Dedicated parser found more complete results:', {
          previousCount: mergedData.top_selected_ideas?.length || 0,
          newCount: dedicatedTop10Result.length,
          ranks: dedicatedTop10Result.map(idea => idea.rank)
        });
        mergedData.top_selected_ideas = dedicatedTop10Result;
      }
    }
    
    console.log('TrizIdeaParser - Final merged data:', {
      mergedDataKeys: Object.keys(mergedData),
      hasTopSelectedIdeas: !!mergedData.top_selected_ideas,
      topSelectedIdeasLength: mergedData.top_selected_ideas?.length,
      hasPrinciples: !!mergedData.principles,
      principlesLength: mergedData.principles?.length,
      finalTopSelectedIdeasRanks: mergedData.top_selected_ideas?.map((idea: any) => idea.rank).sort((a: number, b: number) => a - b),
      finalTopSelectedIdeasDetails: mergedData.top_selected_ideas?.map((idea: any) => ({
        rank: idea.rank,
        name: idea.idea_name || idea.name || 'Unnamed',
        hasScore: !!idea.overall_score
      }))
    });

    const sections: { [key: string]: Idea[] } = {};
    let totalIdeas = 0;

    const ideaConverter = (ideas: any[], sectionName: string): Idea[] => {
      if (!ideas || !Array.isArray(ideas)) return [];
      return ideas.map((idea, index) => ({
        section: sectionName,
        ideaNumber: index + 1,
        ideaName: idea.idea_name || idea.ideaName || idea.name || idea.title || "Untitled Idea",
        description: idea.description || "",
        implementation: idea.implementation || "",
        benefits: Array.isArray(idea.benefits) ? idea.benefits.join(', ') : (idea.benefits || undefined),
        principles: Array.isArray(idea.trizPrinciples) ? idea.trizPrinciples.join(', ') : (idea.trizPrinciples || ""),
        combination: Array.isArray(idea.methods_combined) ? idea.methods_combined.join(' + ') : (idea.methods_combined || undefined),
        synergy: idea.synergy_effect || idea.synergy || "",
      }));
    };

    const ideaFields: { key: keyof ParsedData; name: string }[] = [
      { key: 'function_based_ideas', name: 'Function-Based Ideas' },
      { key: 'system_operator_ideas', name: 'System Operator Ideas' },
      { key: 'bottleneck_solution_ideas', name: 'Bottleneck Solution Ideas' },
      { key: 'benchmark_based_ideas', name: 'Benchmark-Based Ideas' },
      { key: 'framework_based_ideas', name: 'Framework-Based Ideas' },
      { key: 'custom_combination_ideas', name: 'Custom Combination Ideas' },
      { key: 'combination_ideas', name: 'Combination Ideas' },
      { key: 'triz_blending_ideas', name: 'TRIZ Blending Ideas' },
      { key: 'trisociation_ideas', name: 'Trisociation Ideas' },
      { key: 'enhanced_idea', name: 'Enhanced Ideas' },
      { key: 'principles', name: 'TRIZ Principle-Based Ideas' },
      { key: 'improved_ideas', name: 'Improved Ideas' },
    ];

    // Process regular idea fields
    ideaFields.forEach(field => {
      try {
        const ideas = mergedData[field.key] as any[];
        if (ideas && Array.isArray(ideas)) {
          if (field.key === 'principles') {
            const principleIdeas = ideas.flatMap((p: any) => {
              // Handle both 'ideas' and 'breakthrough_applications' structure
              const applications = p.ideas || p.breakthrough_applications || [];
              if (Array.isArray(applications)) {
                return applications.map((idea: any) => ({ ...idea, section: p.principle_name || `Principle ${p.principle_number}` }));
              }
              return [];
            });
            sections[field.name] = ideaConverter(principleIdeas, field.name);
          } else {
            sections[field.name] = ideaConverter(ideas, field.name);
          }
          totalIdeas += sections[field.name]?.length || 0;
        }
      } catch (error) {
        console.error(`Error processing field ${field.key}:`, error);
        sections[field.name] = [];
      }
    });

    // Process TRIZ principles (different structure)
    if (mergedData.principles && Array.isArray(mergedData.principles)) {
      const trizPrincipleIdeas: Idea[] = [];
      
      try {
        mergedData.principles.forEach((principle: any) => {
          if (principle && typeof principle === 'object') {
            const applications = principle.breakthrough_applications || principle.ideas || [];
            if (Array.isArray(applications)) {
              applications.forEach((app: any, index: number) => {
                trizPrincipleIdeas.push({
                  section: `Principle ${principle.principle_number || 'Unknown'}: ${principle.principle_name || 'Unknown'}`,
                  ideaNumber: index + 1,
                  ideaName: app.idea_name || app.name || "Untitled Idea",
                  description: app.description || "",
                  implementation: app.implementation || "",
                  benefits: "",
                  principles: `${principle.principle_number || 'Unknown'}: ${principle.principle_name || 'Unknown'}`,
                  combination: "",
                  synergy: app.contradiction_resolved || app.synergy || ""
                });
              });
            }
          }
        });
        
        if (trizPrincipleIdeas.length > 0) {
          sections["TRIZ Principles"] = trizPrincipleIdeas;
          totalIdeas += trizPrincipleIdeas.length;
        }
      } catch (error) {
        console.error('Error processing TRIZ principles:', error);
      }
    }

    let top10Ideas: any[] = [];
    try {
      console.log('TrizIdeaParser - Processing top 10 ideas:', {
        hasTopSelectedIdeas: !!mergedData.top_selected_ideas,
        topSelectedIdeasType: typeof mergedData.top_selected_ideas,
        topSelectedIdeasLength: mergedData.top_selected_ideas?.length,
        topSelectedIdeasSample: mergedData.top_selected_ideas?.slice(0, 2),
        allMergedDataKeys: Object.keys(mergedData),
        mergedDataSample: Object.keys(mergedData).slice(0, 10)
      });
      
      if (mergedData.top_selected_ideas && Array.isArray(mergedData.top_selected_ideas)) {
        top10Ideas = mergedData.top_selected_ideas.map((idea: any) => ({
          ...idea,
          name: idea.idea_name || idea.name || "Untitled Idea", // Try idea_name first since that's what the JSON contains
        }));
        
        console.log('TrizIdeaParser - Successfully processed top 10 ideas:', {
          count: top10Ideas.length,
          ranks: top10Ideas.map(idea => idea.rank).sort((a, b) => a - b),
          firstIdea: top10Ideas[0],
          firstIdeaKeys: top10Ideas[0] ? Object.keys(top10Ideas[0]) : [],
          firstIdeaName: top10Ideas[0]?.name,
          lastIdea: top10Ideas[top10Ideas.length - 1],
          lastIdeaName: top10Ideas[top10Ideas.length - 1]?.name,
          allIdeaNames: top10Ideas.map(idea => `Rank ${idea.rank}: ${idea.name}`)
        });
      } else {
        console.warn('TrizIdeaParser - No top_selected_ideas found or not an array');
        top10Ideas = [];
      }
    } catch (error) {
      console.error('Error processing top 10 ideas:', error);
      top10Ideas = [];
    }

    // Extract additional data structures from the merged data
    const result: ParsedData = {
      sections,
      totalIdeas,
      top10Ideas,
      principles: mergedData.principles || [],
      market_intelligence_summary: mergedData.market_intelligence_summary,
      competitive_landscape: mergedData.competitive_landscape,
      customer_intelligence: mergedData.customer_intelligence,
      technology_landscape: mergedData.technology_landscape,
      market_opportunities: mergedData.market_opportunities,
      strategic_recommendations: mergedData.strategic_recommendations,
      ...mergedData,
    };

    return result;
  }

  /**
   * Public method to parse only the top 10 ideas from text
   * Useful when you only need the top 10 ideas and want to bypass other parsing
   */
  public parseOnlyTop10Ideas(text: string): { top10Ideas: Top10Idea[], summary: any } {
    console.log('TrizIdeaParser - parseOnlyTop10Ideas - Starting focused top 10 extraction');
    
    const top10Ideas = this.parseTop10Ideas(text);
    
    // Generate summary
    const ranks = top10Ideas.map(idea => idea.rank);
    const missingRanks = Array.from({length: 10}, (_, i) => i + 1).filter(rank => !ranks.includes(rank));
    
    const summary = {
      totalFound: top10Ideas.length,
      ranks: ranks.sort((a, b) => a - b),
      missingRanks,
      isComplete: missingRanks.length === 0 && top10Ideas.length === 10,
      ideaSummary: top10Ideas.map(idea => ({
        rank: idea.rank,
        name: idea.name,
        score: idea.overall_score,
        source: idea.original_source
      }))
    };
    
    console.log('TrizIdeaParser - parseOnlyTop10Ideas - Summary:', summary);
    
    return { top10Ideas, summary };
  }

  private extractJsonObjects(text: string): any[] {
    const jsonObjects: any[] = [];
    const topSelectedIdeasBlocks: any[] = [];
    const allTopSelectedIdeasMatches: any[] = [];
    
    // First, try to extract JSON blocks using the ```json``` pattern
    const jsonBlockRegex = /```json\s*([\s\S]*?)```/g;
    let match;
    
    console.log('TrizIdeaParser - extractJsonObjects - Input text length:', text.length);
    
    // Pre-scan the text to find all occurrences of "top_selected_ideas"
    const topSelectedIdeasRegex = /"top_selected_ideas"\s*:\s*\[/g;
    let topSelectedIdeasMatch;
    let topSelectedIdeasCount = 0;
    
    while ((topSelectedIdeasMatch = topSelectedIdeasRegex.exec(text)) !== null) {
      topSelectedIdeasCount++;
      const contextStart = Math.max(0, topSelectedIdeasMatch.index - 200);
      const contextEnd = Math.min(text.length, topSelectedIdeasMatch.index + 500);
      const context = text.substring(contextStart, contextEnd);
      
      allTopSelectedIdeasMatches.push({
        matchNumber: topSelectedIdeasCount,
        index: topSelectedIdeasMatch.index,
        context: context,
        containsRank1: context.includes('"rank": 1') || context.includes('"rank":1'),
        containsRank6: context.includes('"rank": 6') || context.includes('"rank":6'),
        containsRank10: context.includes('"rank": 10') || context.includes('"rank":10')
      });
    }
    
    console.log('TrizIdeaParser - Pre-scan found', topSelectedIdeasCount, 'top_selected_ideas occurrences');
    console.log('TrizIdeaParser - Pre-scan details:', allTopSelectedIdeasMatches);
    
    // Extract all JSON blocks first
    const allJsonBlocks: { content: string, index: number }[] = [];
    while ((match = jsonBlockRegex.exec(text)) !== null) {
      allJsonBlocks.push({
        content: match[1].trim(),
        index: match.index
      });
    }
    
    console.log(`TrizIdeaParser - Found ${allJsonBlocks.length} JSON blocks total`);
    
    // Process each JSON block
    allJsonBlocks.forEach((block, blockIndex) => {
      const jsonContent = block.content;
      const blockNumber = blockIndex + 1;
      
      console.log(`TrizIdeaParser - extractJsonObjects - Found JSON block ${blockNumber}:`, {
        length: jsonContent.length,
        containsTopSelectedIdeas: jsonContent.includes('top_selected_ideas'),
        preview: jsonContent.substring(0, 200)
      });
      
      // Try to parse the JSON block
      try {
        const parsedObject = JSON.parse(jsonContent);
        if (parsedObject && typeof parsedObject === 'object') {
          const objectKeys = Object.keys(parsedObject);
          console.log(`TrizIdeaParser - extractJsonObjects - Successfully parsed JSON block ${blockNumber}:`, {
            objectKeys: objectKeys.slice(0, 20),
            hasTopSelectedIdeas: !!parsedObject.top_selected_ideas,
            topSelectedIdeasLength: parsedObject.top_selected_ideas?.length,
            agentName: parsedObject.agent_name,
            selectionRange: parsedObject.selection_range
          });
          
          // Special logging for top_selected_ideas blocks
          if (parsedObject.top_selected_ideas) {
            const ranks = parsedObject.top_selected_ideas.map((idea: any) => idea.rank);
            console.log(`TrizIdeaParser - TOP_SELECTED_IDEAS BLOCK ${blockNumber}:`, {
              agentName: parsedObject.agent_name,
              selectionRange: parsedObject.selection_range,
              ideasCount: parsedObject.top_selected_ideas.length,
              ranks: ranks,
              ranksRange: `${Math.min(...ranks)}-${Math.max(...ranks)}`,
              firstIdea: parsedObject.top_selected_ideas[0],
              lastIdea: parsedObject.top_selected_ideas[parsedObject.top_selected_ideas.length - 1]
            });
            
            topSelectedIdeasBlocks.push({
              blockNumber,
              agentName: parsedObject.agent_name,
              selectionRange: parsedObject.selection_range,
              ranks,
              ideas: parsedObject.top_selected_ideas,
              fullObject: parsedObject
            });
          }
          
          jsonObjects.push(parsedObject);
        }
      } catch (e) {
        console.log(`TrizIdeaParser - extractJsonObjects - Failed to parse JSON block ${blockNumber}:`, {
          error: (e as Error).message,
          containsTopSelectedIdeas: jsonContent.includes('top_selected_ideas')
        });
        
        // If it contains top_selected_ideas, try to extract and fix the JSON
        if (jsonContent.includes('top_selected_ideas')) {
          console.log('TrizIdeaParser - extractJsonObjects - Attempting to repair malformed JSON with top_selected_ideas');
          const repairedJson = this.repairMalformedJson(jsonContent);
          if (repairedJson) {
            console.log('TrizIdeaParser - extractJsonObjects - Successfully repaired JSON with top_selected_ideas');
            jsonObjects.push(repairedJson);
          }
        }
      }
    });
    
    // Summary of all top_selected_ideas blocks found
    console.log('TrizIdeaParser - TOP_SELECTED_IDEAS BLOCKS SUMMARY:', {
      totalBlocksFound: topSelectedIdeasBlocks.length,
      blockNumbers: topSelectedIdeasBlocks.map(block => block.blockNumber),
      allRanks: topSelectedIdeasBlocks.flatMap(block => block.ranks).sort((a, b) => a - b),
      blockDetails: topSelectedIdeasBlocks.map(block => ({
        blockNumber: block.blockNumber,
        agentName: block.agentName,
        selectionRange: block.selectionRange,
        ranksRange: `${Math.min(...block.ranks)}-${Math.max(...block.ranks)}`,
        ideasCount: block.ideas.length
      })),
      expectedBlocks: 2,
      foundAllIdeas: topSelectedIdeasBlocks.flatMap(block => block.ranks).length === 10
    });
    
    // If we didn't find JSON blocks with the ```json``` pattern, fall back to brace counting
    if (jsonObjects.length === 0) {
      console.log('TrizIdeaParser - extractJsonObjects - No ```json``` blocks found, falling back to brace counting');
      return this.extractJsonObjectsByBraceCount(text);
    }
    
    console.log('TrizIdeaParser - extractJsonObjects - Final summary:', {
      totalObjectsExtracted: jsonObjects.length,
      objectsWithTopSelectedIdeas: jsonObjects.filter(obj => obj.top_selected_ideas).length,
      totalTop10Ideas: jsonObjects.reduce((sum, obj) => sum + (obj.top_selected_ideas?.length || 0), 0),
      allObjectKeys: jsonObjects.map(obj => Object.keys(obj).slice(0, 10))
    });
    
    return jsonObjects;
  }
  
  private tryToFindMissingTopSelectedIdeasBlock(text: string, jsonObjects: any[], missingRanks: string): void {
    console.log('TrizIdeaParser - tryToFindMissingTopSelectedIdeasBlock - Searching for ranks:', missingRanks);
    
    // Search for patterns that might indicate the missing block
    const searchRank = missingRanks === '1-5' ? '1' : '6';
    const rankPattern = new RegExp(`"rank"\\s*:\\s*${searchRank}`, 'g');
    
    let match;
    while ((match = rankPattern.exec(text)) !== null) {
      const contextStart = Math.max(0, match.index - 2000);
      const contextEnd = Math.min(text.length, match.index + 2000);
      const context = text.substring(contextStart, contextEnd);
      
      console.log('TrizIdeaParser - Found rank', searchRank, 'at position', match.index);
      console.log('TrizIdeaParser - Context around rank', searchRank, ':', context.substring(0, 500));
      
      // Try to find the containing JSON block
      const jsonBlockMatch = context.match(/```json\s*([\s\S]*?)```/);
      if (jsonBlockMatch) {
        console.log('TrizIdeaParser - Found JSON block containing rank', searchRank);
        try {
          const parsedObject = JSON.parse(jsonBlockMatch[1].trim());
          if (parsedObject && parsedObject.top_selected_ideas) {
            console.log('TrizIdeaParser - Successfully parsed missing block:', {
              ranks: parsedObject.top_selected_ideas.map((idea: any) => idea.rank),
              count: parsedObject.top_selected_ideas.length
            });
            jsonObjects.push(parsedObject);
            return;
          }
        } catch (e) {
          console.log('TrizIdeaParser - Failed to parse found JSON block:', (e as Error).message);
        }
      }
    }
    
    console.log('TrizIdeaParser - Could not find missing block with ranks', missingRanks);
  }

  private mergeTopSelectedIdeas(jsonObjects: any[]): any[] {
    console.log('TrizIdeaParser - mergeTopSelectedIdeas - Starting merge of top_selected_ideas from multiple objects');
    
    const allTopSelectedIdeas: any[] = [];
    const agentSummary: any[] = [];
    
    jsonObjects.forEach((obj, index) => {
      if (obj && obj.top_selected_ideas && Array.isArray(obj.top_selected_ideas)) {
        const agentInfo = {
          index,
          agentName: obj.agent_name || `Object ${index + 1}`,
          selectionRange: obj.selection_range || 'Unknown',
          ideasCount: obj.top_selected_ideas.length,
          ranks: obj.top_selected_ideas.map((idea: any) => idea.rank).sort((a: number, b: number) => a - b)
        };
        
        agentSummary.push(agentInfo);
        
        console.log(`TrizIdeaParser - Processing ideas from ${agentInfo.agentName}:`, {
          selectionRange: agentInfo.selectionRange,
          count: agentInfo.ideasCount,
          ranks: agentInfo.ranks
        });
        
        // Add all ideas from this object
        allTopSelectedIdeas.push(...obj.top_selected_ideas);
      }
    });
    
    // Sort by rank to ensure proper ordering
    allTopSelectedIdeas.sort((a, b) => a.rank - b.rank);
    
    // Validate completeness
    const ranks = allTopSelectedIdeas.map(idea => idea.rank);
    const uniqueRanks = [...new Set(ranks)];
    const missingRanks: number[] = [];
    for (let i = 1; i <= 10; i++) {
      if (!ranks.includes(i)) {
        missingRanks.push(i);
      }
    }
    
    console.log('TrizIdeaParser - mergeTopSelectedIdeas - Merge complete:', {
      totalAgents: agentSummary.length,
      agentSummary,
      totalIdeasMerged: allTopSelectedIdeas.length,
      ranks: ranks,
      uniqueRanks,
      missingRanks,
      isComplete: missingRanks.length === 0 && allTopSelectedIdeas.length === 10
    });
    
    if (missingRanks.length > 0) {
      console.warn('TrizIdeaParser - WARNING: After merge, still missing ranks:', missingRanks);
    }
    
    return allTopSelectedIdeas;
  }

  /**
   * Simplified parser for top 10 ideas only using the same extractField method
   */
  public parseTop10Ideas(text: string): Top10Idea[] {
    console.log('TrizIdeaParser - parseTop10Ideas - Starting simplified extraction');
    
    const top10Ideas: Top10Idea[] = [];
    
    // Look for each rank from 1-10 using simple pattern matching
    for (let rank = 1; rank <= 10; rank++) {
      const ideaPattern = new RegExp(`"rank"\\s*:\\s*${rank}[\\s\\S]*?(?="rank"\\s*:\\s*${rank + 1}|$)`, 's');
      const match = text.match(ideaPattern);
      
      if (match) {
        const searchText = match[0];
        
        // Helper function to extract fields using the same method as description
        const extractField = (fieldName: string, isArray: boolean = false): any => {
          const patterns = [
            new RegExp(`"${fieldName}"\\s*:\\s*(${isArray ? '\\[[^\\]]*\\]' : '"[^"]*"'})`, 's'),
            new RegExp(`"${fieldName}"\\s*:\\s*(${isArray ? '\\[(?:[^\\[\\]]*|\\[[^\\]]*\\])*\\]' : '"(?:[^"\\\\]|\\\\.)*"'})`, 's')
          ];
          
          for (const pattern of patterns) {
            const fieldMatch = searchText.match(pattern);
            if (fieldMatch) {
              try {
                return JSON.parse(fieldMatch[1]);
              } catch (e) {
                if (!isArray) {
                  return fieldMatch[1].replace(/^"|"$/g, '').replace(/\\"/g, '"');
                }
              }
            }
          }
          return isArray ? [] : '';
        };
        
        const extractNumberField = (fieldName: string): number => {
          const fieldRegex = new RegExp(`"${fieldName}"\\s*:\\s*([\\d.]+)`);
          const fieldMatch = searchText.match(fieldRegex);
          return fieldMatch ? parseFloat(fieldMatch[1]) : 0;
        };
        
        const extractObjectField = (fieldName: string): any => {
          const objectPattern = new RegExp(`"${fieldName}"\\s*:\\s*(\\{[^{}]*(?:\\{[^{}]*\\}[^{}]*)*\\})`, 's');
          const objectMatch = searchText.match(objectPattern);
          if (objectMatch) {
            try {
              return JSON.parse(objectMatch[1]);
            } catch (e) {
              console.log(`Failed to parse ${fieldName} object for rank ${rank}`);
            }
          }
          return undefined;
        };
        
        const extractArrayOfObjects = (fieldName: string): any[] => {
          const arrayPattern = new RegExp(`"${fieldName}"\\s*:\\s*(\\[(?:[^\\[\\]]*|\\{[^{}]*\\})*\\])`, 's');
          const arrayMatch = searchText.match(arrayPattern);
          if (arrayMatch) {
            try {
              return JSON.parse(arrayMatch[1]);
            } catch (e) {
              console.log(`Failed to parse ${fieldName} array for rank ${rank}`);
            }
          }
          return [];
        };

        // Extract idea name
        const ideaName = extractField('idea_name') || extractField('name') || `Top Innovation ${rank}`;
        
        // Build the idea object using simple extraction
        const ideaObj: Top10Idea = {
          rank: rank,
          name: ideaName,
          description: extractField('description') || '',
          original_source: extractField('original_source') || 'Unknown',
          overall_score: extractNumberField('overall_score') || 8.0,
          key_strengths: extractField('key_strengths', true) || [],
          areas_for_improvement: extractField('areas_for_improvement', true) || [],
          implementation_complexity: extractField('implementation_complexity') || 'Medium',
          innovation_level: extractField('innovation_level') || 'High',
          risk_assessment: extractField('risk_assessment') || 'Medium',
          quality_threshold_check: extractField('quality_threshold_check') || 'Passed',
          individual_scores: extractArrayOfObjects('individual_scores') || [],
          financial_analysis: extractObjectField('financial_analysis'),
          market_analysis: extractObjectField('market_analysis')
        };
        
        top10Ideas.push(ideaObj);
        console.log(`TrizIdeaParser - parseTop10Ideas - Extracted idea rank ${rank}: ${ideaName}`);
      }
    }
    
    // Sort by rank to ensure proper order
    top10Ideas.sort((a, b) => a.rank - b.rank);
    
    console.log('TrizIdeaParser - parseTop10Ideas - Final result:', {
      totalFound: top10Ideas.length,
      ranks: top10Ideas.map(idea => idea.rank)
    });
    
    return top10Ideas;
  }

  /**
   * Helper method to repair malformed JSON
   */
  private repairMalformedJson(jsonContent: string): any | null {
    try {
      // Extract the top_selected_ideas section manually
      const topSelectedIdeasMatch = jsonContent.match(/"top_selected_ideas"\s*:\s*\[([\s\S]*?)\]\s*[,}]/);
      if (!topSelectedIdeasMatch) {
        return null;
      }
      
      const topSelectedIdeasContent = topSelectedIdeasMatch[1];
      
      // Try to parse just the top_selected_ideas array
      try {
        const topSelectedIdeas = JSON.parse(`[${topSelectedIdeasContent}]`);
        console.log('TrizIdeaParser - repairMalformedJson - Successfully extracted top_selected_ideas:', {
          count: topSelectedIdeas.length,
          firstItem: topSelectedIdeas[0]
        });
        
        return {
          top_selected_ideas: topSelectedIdeas
        };
      } catch (arrayError) {
        console.log('TrizIdeaParser - repairMalformedJson - Failed to parse top_selected_ideas array:', (arrayError as Error).message);
        
        // Try to extract individual ideas
        const ideas = [];
        const ideaRegex = /\{\s*"rank"\s*:\s*(\d+)[\s\S]*?\}/g;
        let ideaMatch;
        
        while ((ideaMatch = ideaRegex.exec(topSelectedIdeasContent)) !== null) {
          try {
            const idea = JSON.parse(ideaMatch[0]);
            ideas.push(idea);
          } catch (ideaError) {
            console.log('TrizIdeaParser - repairMalformedJson - Failed to parse individual idea:', (ideaError as Error).message);
          }
        }
        
        if (ideas.length > 0) {
          console.log('TrizIdeaParser - repairMalformedJson - Extracted individual ideas:', ideas.length);
          return {
            top_selected_ideas: ideas
          };
        }
      }
    } catch (error) {
      console.log('TrizIdeaParser - repairMalformedJson - Repair failed:', (error as Error).message);
    }
    
    return null;
  }
  
  private extractJsonObjectsByBraceCount(text: string): any[] {
    const jsonObjects: any[] = [];
    let braceCount = 0;
    let startIndex = -1;
    let inString = false;
    let isEscaped = false;

    const cleanedText = text.replace(/```json/g, '').replace(/```/g, '');
    
    console.log('TrizIdeaParser - extractJsonObjectsByBraceCount - Starting brace count extraction');

    for (let i = 0; i < cleanedText.length; i++) {
      const char = cleanedText[i];

      if (inString) {
        if (char === '"' && !isEscaped) {
          inString = false;
        }
        isEscaped = char === '\\' && !isEscaped;
        continue;
      }

      if (char === '"') {
        inString = true;
        isEscaped = false;
      } else if (char === '{') {
        if (braceCount === 0) {
          startIndex = i;
        }
        braceCount++;
      } else if (char === '}') {
        braceCount--;
        if (braceCount === 0 && startIndex !== -1) {
          const potentialJson = cleanedText.substring(startIndex, i + 1);
          try {
            const parsedObject = JSON.parse(potentialJson);
            if (parsedObject && typeof parsedObject === 'object') {
              console.log('TrizIdeaParser - extractJsonObjectsByBraceCount - Found valid JSON object:', {
                startIndex,
                endIndex: i,
                length: potentialJson.length,
                hasTopSelectedIdeas: !!parsedObject.top_selected_ideas,
                topSelectedIdeasCount: parsedObject.top_selected_ideas?.length,
                agentName: parsedObject.agent_name,
                objectKeys: Object.keys(parsedObject).slice(0, 10)
              });
              jsonObjects.push(parsedObject);
            }
            startIndex = -1;
          } catch (e) {
            console.log('TrizIdeaParser - extractJsonObjectsByBraceCount - Failed to parse potential JSON:', {
              error: (e as Error).message,
              startIndex,
              endIndex: i,
              preview: potentialJson.substring(0, 100)
            });
            startIndex = -1;
          }
        }
      }
    }
    
    console.log('TrizIdeaParser - extractJsonObjectsByBraceCount - Extraction complete:', {
      totalObjectsFound: jsonObjects.length,
      objectsWithTopSelectedIdeas: jsonObjects.filter(obj => obj.top_selected_ideas).length,
      totalTop10Ideas: jsonObjects.reduce((sum, obj) => sum + (obj.top_selected_ideas?.length || 0), 0)
    });
    
    return jsonObjects;
  }
}

/**
 * Standalone function to parse only top 10 ideas from TRIZ agent response
 * @param text - The text containing the TRIZ agent response
 * @returns Object containing the parsed top 10 ideas and summary
 */
export function parseTop10IdeasOnly(text: string): { top10Ideas: Top10Idea[], summary: any } {
  const parser = new TrizIdeaParser();
  return parser.parseOnlyTop10Ideas(text);
}

export default TrizIdeaParser;